import React from 'react';
import { ContextProvider, useStateContext } from "./contexts/ContextProvider.jsx";
import { ConfigProvider, theme } from "antd";
import { getComponentTheme, getThemeToken } from "./theme";
import { ToastContainer } from "react-toastify";
import { RouterProvider } from "react-router-dom";
import {
    CheckCircleFilled,
    CloseCircleFilled,
    WarningFilled,
    InfoCircleFilled,
} from "@ant-design/icons";
import { PRIMARY } from "./shellTokens";
import router from "./router.jsx";

/**
 * Toast icons, one per type, from @ant-design/icons (already a dependency).
 * Toastify v9 lets the container supply the icon for every toast, so none of
 * the ~156 `toast.*(message)` call sites need an options object. The hue comes
 * from toast.css via `currentColor` on the icon chip, so this map carries
 * no colour of its own.
 */
const TOAST_ICONS = {
    success: <CheckCircleFilled />,
    error: <CloseCircleFilled />,
    // Triangle, not another filled circle: the default accent (#d97706) sits
    // close to STATUS.warn, so shape carries the distinction whatever accent
    // the user has chosen.
    warning: <WarningFilled />,
    info: <InfoCircleFilled />,
};

const AppContent = () => {
    const { darkMode, accentColor } = useStateContext();

    // Sync body background with theme
    React.useEffect(() => {
        const bodyColor = darkMode ? '#0f172a' : '#f1f5f9';
        document.body.style.backgroundColor = bodyColor;
        // Optionally toggle a class for Tailwind 'darkMode: class'
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [darkMode]);

    // Toasts portal to <body>, outside the element the shell's CSS variables are
    // declared on, and PRIMARY is user-chosen so a stylesheet cannot know it.
    // Publishing the accent on <html> lets toast.css read it for the info
    // type and its progress bar without duplicating the palette in JS.
    React.useEffect(() => {
        document.documentElement.style.setProperty(
            "--et-toast-accent",
            accentColor || PRIMARY
        );
    }, [accentColor]);

    return (
        <ConfigProvider
            key={accentColor}
            theme={{
                algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
                token: getThemeToken(darkMode),
                components: getComponentTheme(darkMode)
            }}>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                /* "colored" and "dark" are toastify's own palettes; the shell's
                   palette is applied by toast.css, which themes both modes
                   off the `dark` class on <html>. */
                theme={darkMode ? "dark" : "light"}
                icon={({ type }) => TOAST_ICONS[type] ?? TOAST_ICONS.info}
            />
            <RouterProvider router={router} />
        </ConfigProvider>
    );
};

const App = () => {
    return (
        <ContextProvider>
            <AppContent />
        </ContextProvider>
    );
};

export default App;
